import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatEatDateTime } from '@wodi/domain';
import { OPERATION_NOTE_SECTIONS, type TheatreCaseView } from '@wodi/shared';
import { Button, Dialog, DialogActions, Notice, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { amendOperationNote, signOperationNote } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import styles from './theatre.module.css';
import { errorText } from './words.ts';

// The operation note (docs/plans/phase2-theatre.md §2.6, §5.3): written in fixed provisional
// sections (T11), signed as version 1, then corrected only by amendments with a reason. Every
// version stays readable.

type Sections = Record<string, string>;

const blank = (): Sections => Object.fromEntries(OPERATION_NOTE_SECTIONS.map((s) => [s.key, '']));

function NoteFields({ value, onChange }: { value: Sections; onChange: (next: Sections) => void }) {
  return (
    <>
      {OPERATION_NOTE_SECTIONS.map((s) => (
        <TextArea
          key={s.key}
          label={s.key === 'procedure' ? s.heading : `${s.heading} (optional)`}
          {...(s.key === 'anaesthesia'
            ? { description: 'Free text. Wodi suggests no anaesthetic drug or dose.' }
            : {})}
          value={value[s.key] ?? ''}
          onChange={(text) => {
            onChange({ ...value, [s.key]: text });
          }}
          rows={s.key === 'details' || s.key === 'findings' ? 4 : 2}
        />
      ))}
    </>
  );
}

const asBody = (value: Sections) =>
  OPERATION_NOTE_SECTIONS.map((s) => ({ key: s.key, text: value[s.key] ?? '' }));

function SignForm({ view }: { view: TheatreCaseView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<Sections>(blank);
  const sign = useMutation({
    mutationFn: () => signOperationNote(view.id, asBody(value)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({ title: 'Operation note signed' });
    },
  });
  const ready = online && (value['procedure'] ?? '').trim() !== '' && !sign.isPending;
  return (
    <form
      className={styles.form}
      aria-label="Operation note"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) sign.mutate();
      }}
    >
      <NoteFields value={value} onChange={setValue} />
      <p className={styles.meta}>
        Signing records the note under your name. Corrections after that are amendments with a
        reason.
      </p>
      {sign.error === null ? null : (
        <Notice tone="error">{errorText(sign.error, "The note wasn't signed. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!ready}
          isPending={sign.isPending}
          pendingLabel="Signing…"
        >
          Sign operation note
        </Button>
      </div>
    </form>
  );
}

function AmendDialog({ view, onClose }: { view: TheatreCaseView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const latest = view.operationNote?.versions[0];
  const [value, setValue] = useState<Sections>(() =>
    Object.fromEntries((latest?.sections ?? []).map((s) => [s.key, s.text])),
  );
  const [reason, setReason] = useState('');
  const amend = useMutation({
    mutationFn: () =>
      amendOperationNote(view.id, { sections: asBody(value), reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({ title: 'Operation note amended' });
      onClose();
    },
  });
  const ready =
    online &&
    (value['procedure'] ?? '').trim() !== '' &&
    reason.trim().length >= 5 &&
    !amend.isPending;
  return (
    <Dialog
      title="Amend operation note"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <NoteFields value={value} onChange={setValue} />
      <TextArea
        label="Why the note is amended"
        description="At least 5 characters. Kept with the new version."
        value={reason}
        onChange={setReason}
        rows={2}
      />
      {amend.error === null ? null : (
        <Notice tone="error">
          {errorText(amend.error, "The note wasn't amended. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={amend.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            amend.mutate();
          }}
        >
          Save amendment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OperationNoteSection({
  view,
  mayWrite,
}: {
  view: TheatreCaseView;
  mayWrite: boolean;
}) {
  const online = useOnline();
  const [amending, setAmending] = useState(false);
  const note = view.operationNote;
  if (note === null) {
    if (view.status === 'booked' || view.status === 'cancelled') {
      return <p className={styles.meta}>The note is written once the case is in theatre.</p>;
    }
    return mayWrite ? (
      <SignForm view={view} />
    ) : (
      <p className={styles.meta}>Not written yet. The surgeon writes and signs it.</p>
    );
  }
  const [latest, ...older] = note.versions;
  return (
    <>
      {latest === undefined ? null : (
        <>
          <p className={styles.meta}>
            {`Version ${String(latest.version)}, ${latest.kind === 'signed' ? 'signed' : 'amended'} ${formatEatDateTime(new Date(latest.at))} by ${latest.by.displayName}${latest.changeReason === null ? '' : `. Reason: ${latest.changeReason}`}`}
          </p>
          {latest.sections
            .filter((s) => s.text !== '')
            .map((s) => (
              <div key={s.key} className={styles.noteSection}>
                <h3 className={styles.noteHeading}>{s.heading}</h3>
                <p className={styles.prose}>{s.text}</p>
              </div>
            ))}
        </>
      )}
      {mayWrite ? (
        <div className={styles.actions}>
          <Button
            isDisabled={!online}
            onPress={() => {
              setAmending(true);
            }}
          >
            Amend note
          </Button>
        </div>
      ) : null}
      {older.length === 0 ? null : (
        <details>
          <summary>{`Earlier versions (${String(older.length)})`}</summary>
          {older.map((v) => (
            <div key={v.version} className={styles.noteSection}>
              <p className={styles.meta}>
                {`Version ${String(v.version)}, ${formatEatDateTime(new Date(v.at))} by ${v.by.displayName}${v.changeReason === null ? '' : `. Reason: ${v.changeReason}`}`}
              </p>
              {v.sections
                .filter((s) => s.text !== '')
                .map((s) => (
                  <p key={s.key} className={styles.prose}>{`${s.heading}: ${s.text}`}</p>
                ))}
            </div>
          ))}
        </details>
      )}
      {amending ? (
        <AmendDialog
          view={view}
          onClose={() => {
            setAmending(false);
          }}
        />
      ) : null}
    </>
  );
}
